export type Lang = "en" | "it" | "fr";

type ExperimentSection = {
	description: string;
	imageUrl?: string;
	title?: string;
};

type Experiment = {
	id: string;
	date: string;
	title: string;
	kicker: string;
	summary: string;
	status: string;
	kind: string;
	experimentSections: ExperimentSection[];
};

type Social = {
	id: string;
	label: string;
	url: string;
};

type NavItem = {
	id: string;
	label: string;
	to: string;
	external?: boolean;
};

/** Grouped by the page / component that owns the strings. */
export type Text = {
	/** SiteLayout, NameIntro, Header, BrandName, Role, HeroSection, search */
	site: {
		metaTitle: string;
		metaDescription: string;
		displayName: [string, string];
		wordmark: string;
		role: string;
		socials: Social[];
		backHome: string;
		contextMenu: string;
		lang: {
			changed: string;
			en: string;
			it: string;
			fr: string;
		};
	};
	/** Header */
	header: {
		searchLabel: string;
		navLabel: string;
		themeToLight: string;
		themeToDark: string;
	};
	/** CommandPalette */
	palette: {
		placeholder: string;
		empty: string;
		loading: string;
		appearance: string;
	};
	/** Footer, ContactForm, search */
	footer: {
		home: string;
		sitemap: string;
		connect: string;
		nav: NavItem[];
		cv: string;
		cvStandard: string;
		cvArtistic: string;
		language: string;
		copyright: string;
		inProgress: string;
		privacy: string;
		contact: {
			label: string;
			name: string;
			email: string;
			message: string;
			send: string;
			sending: string;
			success: string;
			error: string;
		};
	};
	/** Privacy dialog, search */
	privacy: {
		title: string;
		updated: string;
		body: string[];
		close: string;
		policies: string;
	};
	/** HomePage / HeroSection */
	about: {
		kicker: string;
		title: string;
		statementLead: string;
		statementBody: string;
		placeLabel: string;
		places: string[];
		modeLabel: string;
		languageLabel: string;
		languages: string[];
		skillsLabel: string;
		skills: Array<{ title: string; items: string[] }>;
	};
	/** HomePage / TimelineSection, TimelineExperienceList */
	timeline: {
		kicker: string;
		title: string;
		typeLabel: string;
		statusLabel: string;
		stackLabel: string;
		detailsShow: string;
		detailsHide: string;
		experienceKind: Record<ExperienceType, string>;
		experiences: Experience[];
	};
	/** HomePage / BlogSection, BlogPage, BlogPostPage */
	blog: {
		kicker: string;
		title: string;
		intro: string;
		readArchive: string;
		latest: string;
		readArticle: string;
		previous: string;
		next: string;
		chapters: string;
		minutes: string;
		empty: string;
	};
	/** HomePage / LabSection, LabPage, LabDetailPage */
	lab: {
		kicker: string;
		title: string;
		openExperiment: string;
		allExperiments: string;
		empty: string;
		info: string;
		experiments: Experiment[];
	};
	/** NotFoundPage */
	notFound: {
		title: string;
	};
};

export function localeForLang(lang: string) {
	if (lang === "it") return "it-IT";
	if (lang === "fr") return "fr-FR";
	return "en-US";
}

const years = new Date().getFullYear() - 2015;

const socials: Social[] = [
	{ id: "github", label: "GitHub", url: "https://github.com/ArkoMO93" },
	{
		id: "instagram",
		label: "Instagram",
		url: "https://www.instagram.com/marc0_0livieri/",
	},
	{
		id: "linkedin",
		label: "LinkedIn",
		url: "https://www.linkedin.com/in/olivierimarco93/",
	},
	{
		id: "email",
		label: "Email",
		url: "mailto:marco@marcoolivieri.dev",
	},
	{
		id: "unsplash",
		label: "Unsplash",
		url: "https://unsplash.com/@marco_olivieri",
	},
];

const skills: Array<{ title: string; items: string[] }> = [
	{
		title: "IT",
		items: [
			"Software Architecture",
			"Backend Development",
			"Full-Stack Development",
			"REST API Design & Integration",
			"System Integration",
			"Technical Leadership",
			"Legacy System Modernization",
			"Enterprise Application Development",
			"Relational Database Design",
			"SQL Query Optimization",
			"CI/CD",
			"Test Automation",
			"Agile Software Development",
			"AI-assisted Software Development",
			"Performance Optimization",
			"Cross-functional Collaboration",
		],
	},
	{
		title: "Programming Languages",
		items: [
			"TypeScript",
			"JavaScript (ES6+)",
			"C#",
			"VB.NET",
			"SQL (T-SQL)",
			"PowerShell",
			"AL",
			"C/AL",
		],
	},
	{
		title: "Frameworks & Libraries",
		items: [
			"React",
			"Next.js",
			"Vue.js",
			"Nuxt.js",
			"Node.js",
			"Express.js",
			"Jest",
			"Tailwind CSS",
			"GraphQL",
			"Webpack",
			"Vite",
		],
	},
	{
		title: "Databases",
		items: ["Microsoft SQL Server (MSSQL)", "PostgreSQL", "MongoDB"],
	},
];

const cvPath = "/CVs/Marco_Olivieri_Senior_Dev_July_2026.pdf";
const cvArtisticPath = "/CVs/MO_Resume_Edited_EN_02_2026_Artistic.pdf";

const nav: NavItem[] = [
	{ id: "blog", label: "Blog", to: "/blog" },
	{ id: "lab", label: "Lab", to: "/lab" },
];

// TimelineSection + TimelineExperienceList
const experiencesEn: Experience[] = [
	{
		type: "personal",
		time: "June, '93",
		title: "Born",
		location: "Noto, Sicily, IT",
		locationURL: "https://goo.gl/maps/DPCCFkog8cFTmhaD9",
	},
	{
		type: "personal",
		time: "'03 to '06",
		location: "Parmain, Île-de-France, FR",
		locationURL: "https://goo.gl/maps/SYfFht1zJ7vhBB4Q8",
		details: [
			"Lived in France during secondary school years - French became a second native language.",
		],
	},
	{
		type: "personal",
		time: "'06",
		location: "Mirandola, Emilia Romagna, IT",
		locationURL: "https://goo.gl/maps/pzQ1CT41fnvPKeWt8",
	},
	{
		type: "study",
		time: "'07 to '12",
		location: "Finale Emilia, Emilia Romagna, IT",
		locationURL: "https://goo.gl/maps/TecvzmLoFM5Mr7K99",
		title: "High School Morando Morandi",
		titleURL: "https://www.liceomorandi.edu.it/",
		description: "Scientific High School",
		details: [
			"Scientific high school diploma with solid foundations in mathematics and computer science.",
		],
	},
	{
		type: "study",
		time: "'12 to '17",
		location: "Bologna, Emilia Romagna, IT",
		locationURL: "https://goo.gl/maps/TdCgaMMnkuv64qfe7",
		title: "Alma Mater Studiorum - Bologna University",
		titleURL: "https://www.unibo.it/it",
		description: "Computer Software Engineering - not completed",
		details: [
			"Studied Computer Software Engineering while starting a professional career.",
		],
	},
	{
		type: "work",
		time: "June '15 – May '21",
		location: "Mirandola, Emilia Romagna, IT",
		locationURL: "https://goo.gl/maps/pzQ1CT41fnvPKeWt8",
		title: "X Data Net",
		titleURL: "https://xdatanet.com/",
		description: "Software Engineer",
		details: [
			"I developed enterprise solutions on Microsoft NAV / Business Central with modular backend components.",
			"I optimized SQL queries and data structures for reliability and processing time.",
			"I integrated via web services and document-generation utilities for automated workflows.",
			"I gathered requirements from 100+ companies and translated them into technical specifications with stakeholders.",
		],
		technologies:
			"Business Central, AL / C/AL, C#, VB.NET, T-SQL, MSSQL, REST APIs, PowerShell, Azure DevOps, Git",
	},
	{
		type: "study",
		time: "January '21",
		location: "Online",
		title: "Udemy Course",
		titleURL:
			"https://www.udemy.com/share/1013gG3@TaeC727_JLMFHvheljwUFmzVO_K29i9_hWvUfZ5sc7_7AC9jxmaKE1VdgMJk6Luj/",
		description: "The Complete 2021 Web Development Bootcamp",
		details: [
			"Full-stack development foundations: HTML, CSS, JavaScript, TypeScript, Node.js, and modern frontend workflows.",
		],
	},
	{
		type: "work",
		time: "June '21 – May '23",
		location: "Casalecchio di Reno, Emilia Romagna, IT",
		locationURL: "https://goo.gl/maps/wZ21A6pakL8AFGPp7",
		title: "Alterna",
		titleURL: "https://www.alternanet.it/",
		description: "Senior Software Engineer",
		details: [
			"I developed backend components for business-critical systems with automated workflows and data-processing procedures.",
			"I integrated platform integration logic through APIs and structured queries.",
			"While analyzing technical debt, I refactored according to SOLID principles for faster onboarding.",
			"I worked in Agile on sprint planning and continuous delivery; Docker for ERP testing in controlled environments.",
		],
		technologies:
			"Business Central, C#, VB.NET, T-SQL, MSSQL, Docker, Azure DevOps, Git",
	},
	{
		type: "personal",
		time: "April '23",
		location: "Lausanne, Vaud, CH",
		locationURL: "https://www.google.com/maps/place/Lausanne,+Switzerland",
	},
	{
		type: "work",
		time: "June '23 – Present",
		location: "Rolle, Vaud, CH",
		locationURL: "https://maps.app.goo.gl/pD6jDA37WE1vuPpp9",
		title: "Schenk Suisse S.A.",
		titleURL: "https://www.schenk-wine.ch/fr",
		description: "Senior Software Engineer → Tech Lead",
		details: [
			"Developing .NET applications, Microsoft ERP add-ons, and other enterprise applications supporting operational workflows across departments.",
			"I manage the developers team for internal VB.NET software and the Microsoft ERP.",
			"I led the evolution of a legacy VB.NET application toward a React / Node.js architecture.",
			"I handle ERP integration with external platforms through APIs and database interactions.",
			"I automated data checks and workflow validations, reducing daily operational errors.",
			"I integrated AI tools (Cursor, Copilot) into the workflow.",
		],
		technologies:
			"TypeScript, Vue.js, Nuxt.js, Node.js, Express, PostgreSQL, JWT, OAuth, Jest, Business Central, C#, VB.NET, T-SQL, MSSQL, Tailwind, Vite, Azure DevOps, CI/CD",
	},
];

const experiencesIt: Experience[] = [
	{
		type: "personal",
		time: "Giugno, '93",
		title: "Nascita",
		location: "Noto, Sicilia, IT",
		locationURL: "https://goo.gl/maps/DPCCFkog8cFTmhaD9",
	},
	{
		type: "personal",
		time: "'03 al '06",
		location: "Parmain, Île-de-France, FR",
		locationURL: "https://goo.gl/maps/SYfFht1zJ7vhBB4Q8",
		details: [
			"Anni di scuola in Francia - il francese è diventata una seconda lingua madre.",
		],
	},
	{
		type: "personal",
		time: "'06",
		location: "Mirandola, Emilia Romagna, IT",
		locationURL: "https://goo.gl/maps/pzQ1CT41fnvPKeWt8",
	},
	{
		type: "study",
		time: "'07 al '12",
		location: "Finale Emilia, Emilia Romagna, IT",
		locationURL: "https://goo.gl/maps/TecvzmLoFM5Mr7K99",
		title: "Liceo Scientifico Morando Morandi",
		titleURL: "https://www.liceomorandi.edu.it/",
		description: "Liceo scientifico",
		details: [
			"Diploma di liceo scientifico con basi solide in matematica e informatica.",
		],
	},
	{
		type: "study",
		time: "'12 al '17",
		location: "Bologna, Emilia Romagna, IT",
		locationURL: "https://goo.gl/maps/TdCgaMMnkuv64qfe7",
		title: "Alma Mater Studiorum - Università di Bologna",
		titleURL: "https://www.unibo.it/it",
		description: "Ingegneria del software - non terminata",
		details: [
			"Percorso in Ingegneria del software parallelo all'inizio della carriera professionale.",
		],
	},
	{
		type: "work",
		time: "Giu '15 – Mag '21",
		location: "Mirandola, Emilia Romagna, IT",
		locationURL: "https://goo.gl/maps/pzQ1CT41fnvPKeWt8",
		title: "X Data Net",
		titleURL: "https://xdatanet.com/",
		description: "Software Engineer",
		details: [
			"Ho sviluppato soluzioni enterprise su Microsoft NAV / Business Central con componenti backend modulari.",
			"Ho ottimizzato query SQL e strutture dati per affidabilità e tempi di elaborazione.",
			"Ho integrato via web service e utility di generazione documenti per workflow automatici.",
			"Ho raccolto requisiti da oltre 100 aziende e tradotto in specifiche tecniche con gli stakeholder.",
		],
		technologies:
			"Business Central, AL / C/AL, C#, VB.NET, T-SQL, MSSQL, REST APIs, PowerShell, Azure DevOps, Git",
	},
	{
		type: "study",
		time: "Gennaio '21",
		location: "Online",
		title: "Corso Udemy",
		titleURL:
			"https://www.udemy.com/share/1013gG3@TaeC727_JLMFHvheljwUFmzVO_K29i9_hWvUfZ5sc7_7AC9jxmaKE1VdgMJk6Luj/",
		description: "The Complete 2021 Web Development Bootcamp",
		details: [
			"Fondamenti di sviluppo full-stack: HTML, CSS, JavaScript, TypeScript, Node.js e workflow frontend moderni.",
		],
	},
	{
		type: "work",
		time: "Giu '21 – Mag '23",
		location: "Casalecchio di Reno, Emilia Romagna, IT",
		locationURL: "https://goo.gl/maps/wZ21A6pakL8AFGPp7",
		title: "Alterna",
		titleURL: "https://www.alternanet.it/",
		description: "Senior Software Engineer",
		details: [
			"Ho sviluppato componenti backend per sistemi business-critical con workflow e procedure di data processing automatiche.",
			"Ho integrato la logica di integrazione tra piattaforme tramite API e query strutturate.",
			"Analizzando il debito tecnico ho operato in refactoring secondo i principi SOLID per onboarding più rapido.",
			"Ho lavorato in Agile su sprint planning e continuous delivery; Docker per test ERP in ambienti controllati.",
		],
		technologies:
			"Business Central, C#, VB.NET, T-SQL, MSSQL, Docker, Azure DevOps, Git",
	},
	{
		type: "personal",
		time: "Aprile '23",
		location: "Losanna, Vaud, CH",
		locationURL: "https://www.google.com/maps/place/Lausanne,+Switzerland",
	},
	{
		type: "work",
		time: "Giu '23 – Oggi",
		location: "Rolle, Vaud, CH",
		locationURL: "https://maps.app.goo.gl/pD6jDA37WE1vuPpp9",
		title: "Schenk Suisse S.A.",
		titleURL: "https://www.schenk-wine.ch/fr",
		description: "Senior Software Engineer → Tech Lead",
		details: [
			"Sviluppo applicazioni .NET, addon per l'ERP Microsoft e ogni altra applicazione enterprise a supporto dei workflow operativi tra i reparti.",
			"Gestisco il team di sviluppatori per il software VB.NET interno e per l'ERP Microsoft.",
			"Ho guidato l'evoluzione di un'applicazione legacy VB.NET verso un'architettura React / Node.js.",
			"Mi occupo dell'integrazione dell'ERP con piattaforme esterne tramite API e interazioni sul database.",
			"Ho automatizzato il controlli dati e validazioni di workflow, riducendo gli errori operativi quotidiani.",
			"Ho integrato strumenti di AI (Cursor, Copilot) nel flusso di lavoro.",
		],
		technologies:
			"TypeScript, Vue.js, Nuxt.js, Node.js, Express, PostgreSQL, JWT, OAuth, Jest, Business Central, C#, VB.NET, T-SQL, MSSQL, Tailwind, Vite, Azure DevOps, CI/CD",
	},
];

const experiencesFr: Experience[] = [
	{
		type: "personal",
		time: "Juin '93",
		title: "Naissance",
		location: "Noto, Sicile, IT",
		locationURL: "https://goo.gl/maps/DPCCFkog8cFTmhaD9",
	},
	{
		type: "personal",
		time: "'03 à '06",
		location: "Parmain, Île-de-France, FR",
		locationURL: "https://goo.gl/maps/SYfFht1zJ7vhBB4Q8",
		details: [
			"Années scolaires en France - le français est devenu une seconde langue maternelle.",
		],
	},
	{
		type: "personal",
		time: "'06",
		location: "Mirandola, Émilie-Romagne, IT",
		locationURL: "https://goo.gl/maps/pzQ1CT41fnvPKeWt8",
	},
	{
		type: "study",
		time: "'07 à '12",
		location: "Finale Emilia, Émilie-Romagne, IT",
		locationURL: "https://goo.gl/maps/TecvzmLoFM5Mr7K99",
		title: "Lycée scientifique Morando Morandi",
		titleURL: "https://www.liceomorandi.edu.it/",
		description: "Lycée scientifique",
		details: [
			"Diplôme de lycée scientifique avec de solides bases en mathématiques et informatique.",
		],
	},
	{
		type: "study",
		time: "'12 à '17",
		location: "Bologne, Émilie-Romagne, IT",
		locationURL: "https://goo.gl/maps/TdCgaMMnkuv64qfe7",
		title: "Alma Mater Studiorum - Université de Bologne",
		titleURL: "https://www.unibo.it/it",
		description: "Génie logiciel - non terminé",
		details: [
			"Parcours en génie logiciel en parallèle du début de carrière professionnelle.",
		],
	},
	{
		type: "work",
		time: "Juin '15 – Mai '21",
		location: "Mirandola, Émilie-Romagne, IT",
		locationURL: "https://goo.gl/maps/pzQ1CT41fnvPKeWt8",
		title: "X Data Net",
		titleURL: "https://xdatanet.com/",
		description: "Software Engineer",
		details: [
			"J'ai développé des solutions d'entreprise sur Microsoft NAV / Business Central avec des composants backend modulaires.",
			"J'ai optimisé les requêtes SQL et les structures de données pour la fiabilité et les temps de traitement.",
			"J'ai intégré via web service et utilitaires de génération de documents pour des workflows automatisés.",
			"J'ai recueilli les besoins de plus de 100 entreprises et les ai traduits en spécifications techniques avec les parties prenantes.",
		],
		technologies:
			"Business Central, AL / C/AL, C#, VB.NET, T-SQL, MSSQL, REST APIs, PowerShell, Azure DevOps, Git",
	},
	{
		type: "study",
		time: "Janvier '21",
		location: "En ligne",
		title: "Cours Udemy",
		titleURL:
			"https://www.udemy.com/share/1013gG3@TaeC727_JLMFHvheljwUFmzVO_K29i9_hWvUfZ5sc7_7AC9jxmaKE1VdgMJk6Luj/",
		description: "The Complete 2021 Web Development Bootcamp",
		details: [
			"Fondements du développement full-stack : HTML, CSS, JavaScript, TypeScript, Node.js et workflows frontend modernes.",
		],
	},
	{
		type: "work",
		time: "Juin '21 – Mai '23",
		location: "Casalecchio di Reno, Émilie-Romagne, IT",
		locationURL: "https://goo.gl/maps/wZ21A6pakL8AFGPp7",
		title: "Alterna",
		titleURL: "https://www.alternanet.it/",
		description: "Senior Software Engineer",
		details: [
			"J'ai développé des composants backend pour systèmes critiques avec workflows et procédures de traitement de données automatisées.",
			"J'ai intégré la logique d'intégration entre plateformes via API et requêtes structurées.",
			"En analysant la dette technique, j'ai refactorisé selon les principes SOLID pour un onboarding plus rapide.",
			"J'ai travaillé en Agile sur le sprint planning et la continuous delivery ; Docker pour tester l'ERP en environnements contrôlés.",
		],
		technologies:
			"Business Central, C#, VB.NET, T-SQL, MSSQL, Docker, Azure DevOps, Git",
	},
	{
		type: "personal",
		time: "Avril '23",
		location: "Lausanne, Vaud, CH",
		locationURL: "https://www.google.com/maps/place/Lausanne,+Switzerland",
	},
	{
		type: "work",
		time: "Juin '23 – Aujourd'hui",
		location: "Rolle, Vaud, CH",
		locationURL: "https://maps.app.goo.gl/pD6jDA37WE1vuPpp9",
		title: "Schenk Suisse S.A.",
		titleURL: "https://www.schenk-wine.ch/fr",
		description: "Senior Software Engineer → Tech Lead",
		details: [
			"Développement d'applications .NET, d'addons pour l'ERP Microsoft et de toute autre application d'entreprise au service des workflows opérationnels entre les départements.",
			"Je gère l'équipe de développeurs pour le logiciel VB.NET interne et l'ERP Microsoft.",
			"J'ai guidé l'évolution d'une application legacy VB.NET vers une architecture React / Node.js.",
			"Je m'occupe de l'intégration de l'ERP avec des plateformes externes via API et interactions base de données.",
			"J'ai automatisé les contrôles de données et les validations de workflows, réduisant les erreurs opérationnelles quotidiennes.",
			"J'ai intégré des outils d'IA (Cursor, Copilot) dans le flux de travail.",
		],
		technologies:
			"TypeScript, Vue.js, Nuxt.js, Node.js, Express, PostgreSQL, JWT, OAuth, Jest, Business Central, C#, VB.NET, T-SQL, MSSQL, Tailwind, Vite, Azure DevOps, CI/CD",
	},
];

// LabSection, LabPage, LabDetailPage
const experimentsEn: Experiment[] = [
	{
		id: "boids",
		date: "2022-10-04",
		title: "Boids",
		kicker: "Playable systems",
		summary: "Flock simulation. Move the pointer and the swarm reacts.",
		status: "Playable",
		kind: "simulation",
		experimentSections: [
			{
				description:
					'This experiment simulates the flocking behaviour of birds. The algorithm that manages each boid assigns a random direction and velocity at the start, then recalculates motion every frame from three rules: "separation", "alignment" and "cohesion".',
			},
			{
				description: "Each boid steers to avoid crowding local flockmates.",
				imageUrl:
					"https://upload.wikimedia.org/wikipedia/commons/e/e1/Rule_separation.gif",
				title: "Separation",
			},
			{
				description:
					"Each boid steers toward the average heading of local flockmates.",
				imageUrl:
					"https://upload.wikimedia.org/wikipedia/commons/e/e1/Rule_alignment.gif",
				title: "Alignment",
			},
			{
				description:
					"Each boid steers toward the average position (centre of mass) of local flockmates.",
				imageUrl:
					"https://upload.wikimedia.org/wikipedia/commons/2/2b/Rule_cohesion.gif",
				title: "Cohesion",
			},
		],
	},
	{
		id: "sierpinski",
		date: "2023-01-04",
		title: "Sierpiński",
		kicker: "Chaos game",
		summary:
			"An equilateral triangle that fills itself by repeatedly taking midpoints.",
		status: "Playable",
		kind: "fractal",
		experimentSections: [
			{
				description:
					"The Sierpiński triangle is a fractal with the overall shape of an equilateral triangle, subdivided recursively into smaller equilateral triangles.",
			},
			{
				description:
					"To generate it I pick two random vertices, take the midpoint, then keep drawing the midpoint between the last point and a random vertex.",
				imageUrl:
					"https://upload.wikimedia.org/wikipedia/commons/b/b8/Tri%C3%A2ngulo_de_Sierpinski.gif",
				title: "The algorithm",
			},
		],
	},
	{
		id: "perlin",
		date: "2023-01-16",
		title: "Perlin flow",
		kicker: "Noise field",
		summary:
			"Particles drawn in a Perlin-noise flow field. Every generation leaves a different trail.",
		status: "Playable",
		kind: "generative",
		experimentSections: [
			{
				description:
					"This experiment builds a flow field from Perlin noise, then releases particles that follow it. Each run draws a pattern that will not repeat.",
			},
		],
	},
];

const experimentsIt: Experiment[] = [
	{
		id: "boids",
		date: "2022-10-04",
		title: "Boids",
		kicker: "Sistemi giocabili",
		summary:
			"Simulazione di uno stormo. Muovi il puntatore e lo stormo reagisce.",
		status: "Giocabile",
		kind: "simulazione",
		experimentSections: [
			{
				description:
					'Questo esperimento simula il comportamento di uno stormo di uccelli. L\'algoritmo assegna a ogni "uccello" una velocità e una direzione casuale, poi ricalcola il movimento ogni frame su tre idee: "separazione", "allineamento" e "coesione".',
			},
			{
				description:
					"Ogni componente dello stormo sterza per evitare i vicini.",
				imageUrl:
					"https://upload.wikimedia.org/wikipedia/commons/e/e1/Rule_separation.gif",
				title: "Separazione",
			},
			{
				description:
					"Ogni componente dello stormo sterza per allineare la propria direzione a quella dei vicini.",
				imageUrl:
					"https://upload.wikimedia.org/wikipedia/commons/e/e1/Rule_alignment.gif",
				title: "Allineamento",
			},
			{
				description:
					"Ogni componente dello stormo sterza verso la posizione media (centro di massa) dello stormo locale.",
				imageUrl:
					"https://upload.wikimedia.org/wikipedia/commons/2/2b/Rule_cohesion.gif",
				title: "Coesione",
			},
		],
	},
	{
		id: "sierpinski",
		date: "2023-01-04",
		title: "Sierpiński",
		kicker: "Chaos game",
		summary:
			"Un triangolo equilatero che si riempie da solo prendendo punti medi.",
		status: "Giocabile",
		kind: "frattale",
		experimentSections: [
			{
				description:
					"Il triangolo di Sierpiński è un frattale a forma di triangolo equilatero, suddiviso ricorsivamente in triangoli più piccoli.",
			},
			{
				description:
					"Per generarlo prendo due vertici casuali, ne ricavo il punto medio, poi continuo a disegnare il punto medio tra l'ultimo punto e un vertice casuale.",
				imageUrl:
					"https://upload.wikimedia.org/wikipedia/commons/b/b8/Tri%C3%A2ngulo_de_Sierpinski.gif",
				title: "L'algoritmo",
			},
		],
	},
	{
		id: "perlin",
		date: "2023-01-16",
		title: "Flusso Perlin",
		kicker: "Campo di rumore",
		summary:
			"Particelle disegnate in un campo di rumore di Perlin. Ogni generazione lascia una scia diversa.",
		status: "Giocabile",
		kind: "generativo",
		experimentSections: [
			{
				description:
					"Questo esperimento genera un flusso con l'algoritmo di rumore di Perlin, poi rilascia particelle che lo seguono. Ogni esecuzione disegna un pattern unico.",
			},
		],
	},
];

const experimentsFr: Experiment[] = [
	{
		id: "boids",
		date: "2022-10-04",
		title: "Boids",
		kicker: "Systèmes jouables",
		summary: "Simulation d'une volée. Bougez le pointeur et l'essaim réagit.",
		status: "Jouable",
		kind: "simulation",
		experimentSections: [
			{
				description:
					'Cette expérience simule le comportement d\'une volée d\'oiseaux. L\'algorithme assigne à chaque boid une direction et une vitesse aléatoires, puis recalcule le mouvement à chaque frame selon trois règles : "séparation", "alignement" et "cohésion".',
			},
			{
				description: "Chaque boid s'écarte pour éviter la foule des voisins.",
				imageUrl:
					"https://upload.wikimedia.org/wikipedia/commons/e/e1/Rule_separation.gif",
				title: "Séparation",
			},
			{
				description:
					"Chaque boid s'aligne sur la direction moyenne des voisins.",
				imageUrl:
					"https://upload.wikimedia.org/wikipedia/commons/e/e1/Rule_alignment.gif",
				title: "Alignement",
			},
			{
				description:
					"Chaque boid se dirige vers la position moyenne (centre de masse) de la volée locale.",
				imageUrl:
					"https://upload.wikimedia.org/wikipedia/commons/2/2b/Rule_cohesion.gif",
				title: "Cohésion",
			},
		],
	},
	{
		id: "sierpinski",
		date: "2023-01-04",
		title: "Sierpiński",
		kicker: "Chaos game",
		summary: "Un triangle équilatéral qui se remplit en prenant des milieux.",
		status: "Jouable",
		kind: "fractale",
		experimentSections: [
			{
				description:
					"Le triangle de Sierpiński est une fractale en forme de triangle équilatéral, subdivisé récursivement en triangles plus petits.",
			},
			{
				description:
					"Pour le générer, je prends deux sommets au hasard, j'en tire le milieu, puis je continue à dessiner le milieu entre le dernier point et un sommet aléatoire.",
				imageUrl:
					"https://upload.wikimedia.org/wikipedia/commons/b/b8/Tri%C3%A2ngulo_de_Sierpinski.gif",
				title: "L'algorithme",
			},
		],
	},
	{
		id: "perlin",
		date: "2023-01-16",
		title: "Flux Perlin",
		kicker: "Champ de bruit",
		summary:
			"Des particules dessinées dans un champ de bruit de Perlin. Chaque génération laisse une trace différente.",
		status: "Jouable",
		kind: "génératif",
		experimentSections: [
			{
				description:
					"Cette expérience construit un champ de flux à partir du bruit de Perlin, puis libère des particules qui le suivent. Chaque exécution dessine un motif unique.",
			},
		],
	},
];

const en: Text = {
	// SiteLayout, NameIntro, Header, BrandName, Role, HeroSection, search
	site: {
		metaTitle: "Marco Olivieri · Senior Software Engineer & Artist",
		metaDescription:
			"I work on front-end, back-end, and every end in between. 11 years in, the best moment is still when I put on headphones, hands on the keyboard, and write code.",
		displayName: ["Marco", "Olivieri"],
		wordmark: "m/o",
		role: "Senior Software Engineer",
		socials,
		backHome: "Back home",
		contextMenu: "You are curious... I like it ;)",
		lang: {
			changed: "Language set to English",
			en: "English",
			it: "Italian",
			fr: "French",
		},
	},
	// Header
	header: {
		searchLabel: "Search...",
		navLabel: "Sitemap",
		themeToLight: "Switch to light mode",
		themeToDark: "Switch to dark mode",
	},
	// CommandPalette
	palette: {
		placeholder: "Search pages, writing, experiments...",
		empty: "Nothing matches that search.",
		loading: "Preparing search…",
		appearance: "Appearance",
	},
	// Footer, ContactForm, search
	footer: {
		home: "Home",
		sitemap: "Sitemap",
		connect: "Socials",
		nav,
		cv: "Curriculum",
		cvStandard: "Standard CV",
		cvArtistic: "Artistic CV",
		language: "Language",
		copyright: `© ${new Date().getFullYear()} Marco Olivieri. All rights reserved.`,
		inProgress: "Personal site, always in progress",
		privacy: "Privacy Settings",
		contact: {
			label: "Contact",
			name: "Name",
			email: "Email",
			message: "Message",
			send: "Send",
			sending: "Sending...",
			success: "Message sent. I'll get back to you.",
			error: "Couldn't send the message. Try again or use email.",
		},
	},
	// Privacy.tsx, search
	privacy: {
		title: "Privacy settings",
		updated: "Last updated: October 1, 2026",
		body: [
			"This site does not run advertising or third-party analytics.",
			"It only stores what it needs to remember your language and color choices. Those values live in your device and are not sent to analytics services.",
		],
		close: "Close",
		policies: "Policies",
	},
	// HomePage / HeroSection
	about: {
		kicker: "00 / About me",
		title: "About me",
		statementLead: "I'm Marco, a developer and artist.",
		statementBody: `I work on front-end, back-end, and every end in between. ${years} years in, the best moment is still when I put on headphones, hands on the keyboard, and write code.`,
		placeLabel: "Location",
		places: ["Switzerland", "Worldwide"],
		modeLabel: "Role",
		languageLabel: "Language",
		languages: ["Italian", "French", "English"],
		skillsLabel: "Skills",
		skills,
	},
	// HomePage / TimelineSection, TimelineExperienceList
	timeline: {
		kicker: "01 / Timeline",
		title: "Timeline",
		typeLabel: "Type",
		statusLabel: "Status",
		stackLabel: "Stack",
		detailsShow: "Show details",
		detailsHide: "Hide details",
		experienceKind: {
			personal: "Personal Life",
			study: "Study",
			work: "Work",
		},
		experiences: experiencesEn,
	},
	// HomePage / BlogSection, BlogPage, BlogPostPage
	blog: {
		kicker: "02 / Blog",
		title: "Blog",
		intro:
			"A few places where I publish, test ideas, and keep the conversation going.",
		readArchive: "Read archive",
		latest: "Latest",
		readArticle: "Read article",
		previous: "Previous article",
		next: "Next article",
		chapters: "Chapters",
		minutes: "min read",
		empty: "No post...yet.",
	},
	// HomePage / LabSection, LabPage, LabDetailPage
	lab: {
		kicker: "03 / Lab",
		title: "Lab",
		openExperiment: "Open the experiment",
		allExperiments: "All experiments",
		empty: "No experiments yet.",
		info: "+Info",
		experiments: experimentsEn,
	},
	// NotFoundPage
	notFound: {
		title: "Not Found",
	},
};

const it: Text = {
	...en,
	site: {
		...en.site,
		metaTitle: "Marco Olivieri · Ingegnere software senior e artista",
		metaDescription:
			"Lavoro su prodotti front-end, back-end e tutte le end possibili. 11 anni dopo, il momento migliore resta quando metto le cuffie, le mani sulla tastiera e scrivo codice.",
		role: "Ingegnere software senior",
		backHome: "Torna alla home",
		contextMenu: "Sei curioso... mi piace ;)",
		lang: {
			changed: "Lingua impostata in italiano",
			en: "inglese",
			it: "italiano",
			fr: "francese",
		},
	},
	header: {
		searchLabel: "Cerca...",
		navLabel: "Mappa",
		themeToLight: "Passa alla modalità chiara",
		themeToDark: "Passa alla modalità scura",
	},
	palette: {
		placeholder: "Cerca pagine, scritti, esperimenti...",
		empty: "Nessun risultato per questa ricerca.",
		loading: "Preparazione della ricerca…",
		appearance: "Aspetto",
	},
	footer: {
		...en.footer,
		home: "Home",
		sitemap: "Mappa",
		connect: "Socials",
		cv: "Curriculum",
		cvStandard: "CV standard",
		cvArtistic: "CV artistico",
		language: "Lingua",
		copyright: `© ${new Date().getFullYear()} Marco Olivieri. Tutti i diritti riservati.`,
		inProgress: "Sito personale, sempre in corso",
		privacy: "Impostazioni privacy",
		contact: {
			label: "Contattami",
			name: "Nome",
			email: "Email",
			message: "Messaggio",
			send: "Invia",
			sending: "Invio...",
			success: "Messaggio inviato. Ti rispondo io.",
			error: "Invio non riuscito. Riprova o usa l'email.",
		},
	},
	privacy: {
		title: "Impostazioni privacy",
		updated: "Ultimo aggiornamento: 1 ottobre 2026",
		body: [
			"Questo sito non usa pubblicità né analytics di terze parti.",
			"Salva solo quanto serve per ricordare lingua e la scelta delle preferenze sul colore. I valori restano nel tuo dispositivo e non vanno a servizi di analytics.",
		],
		close: "Chiudi",
		policies: "Policy",
	},
	about: {
		...en.about,
		kicker: "00 / Chi sono",
		title: "Chi sono",
		statementLead: "Sono Marco, uno sviluppatore e artista.",
		statementBody: `Lavoro su prodotti front-end, back-end e tutte le end possibili. ${years} anni dopo, il momento migliore resta quando metto le cuffie, le mani sulla tastiera e scrivo codice.`,
		placeLabel: "Location",
		places: ["Svizzera", "Worldwide"],
		modeLabel: "Ruolo",
		languageLabel: "Lingue",
		languages: ["Italiano", "Francese", "Inglese"],
		skillsLabel: "Skills",
	},
	timeline: {
		kicker: "01 / Timeline",
		title: "Timeline",
		typeLabel: "Tipo",
		statusLabel: "Stato",
		stackLabel: "Stack",
		detailsShow: "Mostra dettagli",
		detailsHide: "Nascondi dettagli",
		experienceKind: {
			personal: "Vita Privata",
			study: "Studio",
			work: "Lavoro",
		},
		experiences: experiencesIt,
	},
	blog: {
		kicker: "02 / Blog",
		title: "Blog",
		intro:
			"Qualche posto dove pubblico, provo idee e tengo aperta la conversazione.",
		readArchive: "Archivio",
		latest: "Ultimo",
		readArticle: "Leggi l'articolo",
		previous: "Articolo precedente",
		next: "Articolo successivo",
		chapters: "Capitoli",
		minutes: "min di lettura",
		empty: "Nessun post...per ora.",
	},
	lab: {
		kicker: "03 / Lab",
		title: "Lab",
		openExperiment: "Apri l'esperimento",
		allExperiments: "Tutti gli esperimenti",
		empty: "Nessun esperimento, per ora.",
		info: "+Info",
		experiments: experimentsIt,
	},
	notFound: {
		title: "Non trovata",
	},
};

const fr: Text = {
	...en,
	site: {
		...en.site,
		metaTitle: "Marco Olivieri · Ingénieur logiciel senior et artiste",
		metaDescription:
			"Je travaille sur le front-end, le back-end et toutes les ends possibles. 11 ans plus tard, le meilleur moment reste quand je mets mon casque, les mains sur le clavier, et que j'écris du code.",
		role: "Ingénieur logiciel senior",
		backHome: "Retour à l'accueil",
		contextMenu: "Tu es curieux... j'aime ça ;)",
		lang: {
			changed: "Langue définie sur le français",
			en: "anglais",
			it: "italien",
			fr: "français",
		},
	},
	header: {
		searchLabel: "Rechercher...",
		navLabel: "Plan",
		themeToLight: "Passer en mode clair",
		themeToDark: "Passer en mode sombre",
	},
	palette: {
		placeholder: "Rechercher pages, textes, expériences...",
		empty: "Aucun résultat pour cette recherche.",
		loading: "Préparation de la recherche…",
		appearance: "Apparence",
	},
	footer: {
		...en.footer,
		home: "Accueil",
		sitemap: "Plan",
		connect: "Socials",
		cv: "Curriculum",
		cvStandard: "CV standard",
		cvArtistic: "CV artistique",
		language: "Langue",
		copyright: `© ${new Date().getFullYear()} Marco Olivieri. Tous droits réservés.`,
		inProgress: "Site personnel, toujours en cours",
		privacy: "Paramètres de confidentialité",
		contact: {
			label: "Écrivez-moi",
			name: "Nom",
			email: "Email",
			message: "Message",
			send: "Envoyer",
			sending: "Envoi...",
			success: "Message envoyé. Je vous répondrai.",
			error: "Envoi impossible. Réessayez ou utilisez l'email.",
		},
	},
	privacy: {
		title: "Paramètres de confidentialité",
		updated: "Dernière mise à jour : 1 octobre 2026",
		body: [
			"Ce site n'utilise ni publicité ni analytics tiers.",
			"Il ne stocke que ce qu'il faut pour mémoriser votre langue et vos choix de couleur. Ces valeurs restent dans votre appareil et ne sont pas envoyées à des services d'analytics.",
		],
		close: "Fermer",
		policies: "Politiques",
	},
	about: {
		...en.about,
		kicker: "00 / À propos",
		title: "À propos",
		statementLead: "Je suis Marco, développeur et artiste.",
		statementBody: `Je travaille sur le front-end, le back-end et toutes les ends possibles. ${years} ans plus tard, le meilleur moment reste quand je mets mon casque, les mains sur le clavier, et que j'écris du code.`,
		placeLabel: "Lieu",
		places: ["Suisse", "Worldwide"],
		modeLabel: "Rôle",
		languageLabel: "Langues",
		languages: ["Italien", "Français", "Anglais"],
		skillsLabel: "Compét.",
	},
	timeline: {
		kicker: "01 / Timeline",
		title: "Timeline",
		typeLabel: "Type",
		statusLabel: "Statut",
		stackLabel: "Stack",
		detailsShow: "Afficher les détails",
		detailsHide: "Masquer les détails",
		experienceKind: {
			personal: "Vie privée",
			study: "Études",
			work: "Travail",
		},
		experiences: experiencesFr,
	},
	blog: {
		kicker: "02 / Blog",
		title: "Blog",
		intro:
			"Quelques endroits où je publie, teste des idées et continue la conversation.",
		readArchive: "Archives",
		latest: "Dernier",
		readArticle: "Lire l'article",
		previous: "Article précédent",
		next: "Article suivant",
		chapters: "Chapitres",
		minutes: "min de lecture",
		empty: "Pas de post...pour l'instant.",
	},
	lab: {
		kicker: "03 / Lab",
		title: "Lab",
		openExperiment: "Ouvrir l'expérience",
		allExperiments: "Toutes les expériences",
		empty: "Pas encore d'expériences.",
		info: "+Info",
		experiments: experimentsFr,
	},
	notFound: {
		title: "Introuvable",
	},
};

export const CV_PATH = cvPath;
export const CV_ARTISTIC_PATH = cvArtisticPath;

export function getText(lang: Lang): Text {
	if (lang === "it") return it;
	if (lang === "fr") return fr;
	return en;
}

export const LANG_STORAGE_KEY = "lang";

export function parseLang(value: unknown): Lang | null {
	if (typeof value !== "string") return null;
	const primary = value.trim().toLowerCase().split(/[-_]/)[0];
	if (primary === "it" || primary === "fr" || primary === "en") return primary;
	return null;
}

export function detectBrowserLang(): Lang {
	if (typeof navigator === "undefined") return "en";
	const candidates = [
		...(navigator.languages ?? []),
		navigator.language,
	].filter(Boolean);
	for (const candidate of candidates) {
		const match = parseLang(candidate);
		if (match) return match;
	}
	return "en";
}

/** Stored preference if set; otherwise the browser/OS language. */
export function readStoredLang(): Lang {
	try {
		const raw = localStorage.getItem(LANG_STORAGE_KEY);
		if (raw) {
			try {
				const parsed = parseLang(JSON.parse(raw));
				if (parsed) return parsed;
			} catch {
				const parsed = parseLang(raw);
				if (parsed) return parsed;
			}
		}
	} catch {
		/* ignore */
	}
	return detectBrowserLang();
}

export function persistLang(lang: Lang) {
	try {
		localStorage.setItem(LANG_STORAGE_KEY, JSON.stringify(lang));
	} catch {
		/* ignore */
	}
}
